/**
* RelytArt
*
* Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International
* Copyright (c) 2022 RelytArt
*
* License: https://docs.google.com/document/d/1UD7jhCYWRXSxhx4A5vMeGVTjujwLM39BmvjwA0eFFfk/edit?usp=sharing
*
*/

// models
import Model from "models/model/Model.js";

// utils
import ValidationUtils from "utils/validation-utils/ValidationUtils.js";

// constants
const GESTURE_HANDLING_DEFAULT = "greedy";
const UI_POSITION_DEFAULT = String("TOP_RIGHT");
const UI_POSITIONS = Array([
  String("TOP_CENTER"),
  String("TOP_LEFT"),
  String("TOP_RIGHT"),
  String("LEFT_TOP"),
  String("RIGHT_TOP"),
  String("LEFT_CENTER"),
  String("RIGHT_CENTER"),
  String("LEFT_BOTTOM"),
  String("RIGHT_BOTTOM"),
  String("BOTTOM_CENTER"),
  String("BOTTOM_LEFT"),
  String("BOTTOM_RIGHT")
]);
const UI_SIZE_DEFAULT = Number("40");

// defaults
const DEFAULT_MAP_PROPERTIES = {
  center: {
    lat: Number("0.0"),
    lng: Number("0.0"),
  },
  zoom: Number("1.0")
};

const DEFAULT_UI_PROPERTIES = {
  disable: false,
  disableFullscreenControl: false,
  disableMapTypeControl: false,
  disableScaleControl: false,
  disableStreetViewControl: false,
  gestureHandling: String(GESTURE_HANDLING_DEFAULT),
  position: String(UI_POSITION_DEFAULT),
  size: Number(UI_SIZE_DEFAULT)
};

class GoogleMap extends Model {
  constructor({
    id = Model.getDefaultId(),
    lastUpdated = Model.getDefaultLastUpdated(),
    mapProperties = { ...DEFAULT_MAP_PROPERTIES },
    tags = [{ model: "GoogleMap" }],
    uiProperties = { ...DEFAULT_UI_PROPERTIES }
  } = {}) {
    super({ id, lastUpdated, tags });
    this.state = {
      ...this.state,
      mapProperties: {
        ...DEFAULT_MAP_PROPERTIES,
        ...mapProperties
      },
      uiProperties: {
        ...DEFAULT_UI_PROPERTIES,
        ...uiProperties
      }
    };
  }

  /*====================
  getters
  ====================*/
  getCenter() {
    return {
      lat: Number(this.state.mapProperties.center.lat),
      lng: Number(this.state.mapProperties.center.lng)
    };
  }
  getUiDisabled() { return Boolean(this.state.uiProperties.disable); }
  getUiDisableFullscreenControl() { return Boolean(this.state.uiProperties.disableFullscreenControl); }
  getUiDisableMapTypeControl() { return Boolean(this.state.uiProperties.disableMapTypeControl); }
  getUiDisableScaleControl() { return Boolean(this.state.uiProperties.disableScaleControl); }
  getUiDisableStreetViewControl() { return Boolean(this.state.uiProperties.disableStreetViewControl); }
  getUiGestureHandling() { return String(this.state.uiProperties.gestureHandling); }
  getUiPosition() { return String(this.state.uiProperties.position); }
  getUiSize() { return Number(this.state.uiProperties.size); }
  getZoom() { return Number(this.state.mapProperties.zoom); }

  getState() {
    return {
      id: this.getId(),
      mapProperties: {
        center: this.getCenter(),
        zoom: this.getZoom()
      },
      tags: this.getTags(),
      uiProperties: {
        disable: this.getUiDisable(),
        disableFullscreenControl: this.getUiDisableFullscreenControl(),
        disableMapTypeControl: this.getUiDisableMapTypeControl(),
        disableScaleControl: this.getUiDisableScaleControl(),
        disableStreetViewControl: this.getUiDisableStreetViewControl(),
        gestureHandling: this.getUiGestureHandling(),
        position: this.getUiPosition(),
        size: this.getUiSize()
      }
    };
  }

  /*====================
  setters
  ====================*/
  setCenter(center) {
    if(ValidationUtils.latLng(center)) {
      this.state.mapProperties.center = {
        lat: Number(center.lat),
        lng: Number(center.lng)
      };
      this.onChange();
    }
    return this.getCenter();
  }

  setUiDisabled(boolean) {
    if(
      ValidationUtils.boolean(boolean) &&
      this.state.uiProperties.disable !== Boolean(boolean)
    ) {
      this.state.uiProperties.disable = Boolean(boolean);
      this.onChange();
    }
    return this.getUiDisabled();
  }

  setUiDisableFullscreenControl(boolean) {
    if(
      ValidationUtils.boolean(boolean) &&
      this.state.uiProperties.disableFullscreenControl !== Boolean(boolean)
    ) {
      this.state.uiProperties.disableFullscreenControl = Boolean(boolean);
      this.onChange();
    }
    return this.getUiDisableFullscreenControl();
  }

  setUiDisableMapTypeControl(boolean) {
    if(
      ValidationUtils.boolean(boolean) &&
      this.state.uiProperties.disableMapTypeControl !== Boolean(boolean)
    ) {
      this.state.uiProperties.disableMapTypeControl = Boolean(boolean);
      this.onChange();
    }
    return this.getUiDisableMapTypeControl();
  }

  setUiDisableScaleControl(boolean) {
    if(
      ValidationUtils.boolean(boolean) &&
      this.state.uiProperties.disableScaleControl !== Boolean(boolean)
    ) {
      this.state.uiProperties.disableScaleControl = Boolean(boolean);
      this.onChange();
    }
    return this.getUiDisableScaleControl();
  }

  setUiDisableStreetViewControl(boolean) {
    if(
      ValidationUtils.boolean(boolean) &&
      this.state.uiProperties.disableStreetViewControl !== Boolean(boolean)
    ) {
      this.state.uiProperties.disableStreetViewControl = Boolean(boolean);
      this.onChange();
    }
    return this.getUiDisableStreetViewControl();
  }

  setUiGestureHandling(gestureHandling) {
    if(
      ValidationUtils.string(gestureHandling) &&
      String(this.state.uiProperties.gestureHandling) !== String(gestureHandling)
    ) {
      this.state.uiProperties.gestureHandling = String(gestureHandling);
      this.onChange();
    }
    return this.getUiGestureHandling();
  }

  setUiPosition(position) {
    if(
      ValidationUtils.string(position) &&
      UI_POSITIONS.includes(String(position)) &&
      String(this.state.uiProperties.position) !== String(position)
    ) {
      this.state.uiProperties.position = String(position);
      this.onChange();
    }
    return this.getUiPosition();
  }

  setUiSize(size) {
    if(
      ValidationUtils.integer(size) &&
      Number(this.state.uiProperties.size) !== Number(size)
    ) {
      this.state.uiProperties.size = Number(size);
      this.onChange();
    }
    return this.getUiSize();
  }

  setZoom(zoom) {
    if(
      ValidationUtils.integer(zoom) &&
      Number(this.state.uiProperties.zoom) !== Number(zoom)
    ) {
      this.state.mapProperties.zoom = Number(zoom);
      this.onChange();
    }
    return this.getZoom();
  }
}
export default GoogleMap;
